import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SubscriptionEditorFields, SubscriptionFormData } from './SubscriptionEditorFields';
import { TIER } from '../utils/tier-constants';
import { validateFilters, getCouplingUpdates, ValidationIssue, prepareSubscriptionFilters } from '../utils/subscriptionValidation';
import { useUpdateAdminSubscription, useDeleteAdminSubscription } from '../hooks/queries/useAdmin';
import { useSubscriptionFromList } from '../hooks/queries/useSubscriptions';

interface Props {
  subscription: any;
  onClose: () => void;
  onSaved: () => void;
}

export const AdminSubscriptionModal: React.FC<Props> = ({
  subscription: initialSubscription,
  onClose,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  
  // Read from shared list cache instead of separate detail query
  const cachedSubscription = useSubscriptionFromList(initialSubscription.subscriptionId, initialSubscription.userId);
  const subscription = cachedSubscription || initialSubscription;

  const [formData, setFormData] = useState<SubscriptionFormData & { chatId: string, userId: string, expiresAt: string }>({
    zipCodes: subscription.zipCodes || [],
    notificationChannels: subscription.notificationChannels || [],
    enabled: subscription.enabled ?? true,
    chatId: subscription.notificationTargets?.telegram?.chatId?.toString() || '',
    userId: subscription.userId || '',
    expiresAt: subscription.expiresAt ? new Date(subscription.expiresAt).toISOString().slice(0, 16) : '',
    uiMetadata: subscription.uiMetadata || { zones: [] },
    filters: {
      minRent: subscription.filters?.minRent?.toString() || '',
      maxRent: subscription.filters?.maxRent?.toString() || '',
      minBaseRent: subscription.filters?.minBaseRent?.toString() || '',
      maxBaseRent: subscription.filters?.maxBaseRent?.toString() || '',
      includeUnknowns: subscription.filters?.includeUnknowns ?? true,
      minRooms: subscription.filters?.minRooms?.toString() || '',
      maxRooms: subscription.filters?.maxRooms?.toString() || '',
      minSpace: subscription.filters?.minSpace?.toString() || '',
      maxSpace: subscription.filters?.maxSpace?.toString() || '',
      wbsFilter: subscription.filters?.wbsFilter || 'ANY',
      wgFilter: subscription.filters?.wgFilter || 'ANY',
      swapFilter: subscription.filters?.swapFilter || 'ANY',
      tempFilter: subscription.filters?.tempFilter || 'ANY',
      providerFilter: subscription.filters?.providerFilter || 'ANY',
      furnishedFilter: subscription.filters?.furnishedFilter || 'ANY',
      closedAreaFilter: subscription.filters?.closedAreaFilter || 'NONE',
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [isBaseRentActive, setIsBaseRentActive] = useState(false);
  const [hasZoneConstraint, setHasZoneConstraint] = useState(false);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    const hasBaseRent = !!(subscription.filters?.minBaseRent || subscription.filters?.maxBaseRent);
    setIsBaseRentActive(hasBaseRent);
  }, [subscription]);

  const handleFieldChange = (field: string | Partial<SubscriptionFormData>, value?: any) => {
    if (typeof field === 'object') {
      setFormData(prev => ({ ...prev, ...field }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFormData(prev => {
      const updates = getCouplingUpdates(field, value, prev.filters);
      const newFilters = { ...prev.filters, ...updates };

      const validationIssues = validateFilters(newFilters, { isBaseRentActive });
      setIssues(validationIssues);

      return { ...prev, filters: newFilters };
    });
  };

  const handleBaseRentToggle = (active: boolean) => {
    setIsBaseRentActive(active);
    const validationIssues = validateFilters(formData.filters, { isBaseRentActive: active });
    setIssues(validationIssues);
  };

  const updateSubscriptionMutation = useUpdateAdminSubscription();
  const deleteSubscriptionMutation = useDeleteAdminSubscription();

  const handleSave = async () => {
    setIsLoading(true);
    setError('');

    const validationIssues = validateFilters(formData.filters, { isBaseRentActive });
    if (validationIssues.some(i => i.type === 'error')) {
      setError('Bitte beheben Sie die markierten Fehler.');
      setIsLoading(false);
      return;
    }

    try {
      const expiresAtMs = formData.expiresAt ? new Date(formData.expiresAt).getTime() : null;
      const payload = {
        userId: formData.userId,
        chatId: formData.chatId ? parseInt(formData.chatId) : null,
        zipCodes: formData.zipCodes,
        notificationChannels: formData.notificationChannels,
        enabled: formData.enabled,
        filters: prepareSubscriptionFilters(formData.filters, { isBaseRentActive }),
        uiMetadata: formData.uiMetadata || null,
        expiresAt: expiresAtMs,
      };

      // -- CHANGE DETECTION --
      if (subscription) {
        const isDirty = (
            payload.userId !== subscription.userId ||
            payload.chatId !== (subscription.notificationTargets?.telegram?.chatId || null) ||
            JSON.stringify(payload.zipCodes) !== JSON.stringify(subscription.zipCodes) ||
            JSON.stringify(payload.notificationChannels) !== JSON.stringify(subscription.notificationChannels) ||
            payload.enabled !== subscription.enabled ||
            JSON.stringify(payload.filters) !== JSON.stringify(subscription.filters) ||
            JSON.stringify(payload.uiMetadata || null) !== JSON.stringify(subscription.uiMetadata || null) ||
            payload.expiresAt !== (subscription.expiresAt || null)
        );

        if (!isDirty) {
            console.log("No changes detected (Admin), skipping PUT");
            onClose();
            return;
        }
      }

      await updateSubscriptionMutation.mutateAsync({ 
          subscriptionId: subscription.subscriptionId, 
          data: payload,
          userId: formData.userId // For invalidation
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Diese Subscription wirklich unwiderruflich löschen?')) return;
    
    setIsLoading(true);
    try {
      await deleteSubscriptionMutation.mutateAsync({
          subscriptionId: subscription.subscriptionId,
          userId: subscription.userId // For invalidation
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError('Fehler beim Löschen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // admin-modal-overlay (AdminPage.css): the generic `.modal-overlay` rule this relied on
    // lived in Settings.css/Upgrade.css, which the redesign removed.
    <div className="admin-modal-overlay">
      <div className="admin-sub-modal-content" role="dialog" aria-modal="true" aria-labelledby="admin-sub-modal-title">
        <button 
          onClick={onClose} 
          className="admin-modal-close"
          title="Schließen"
        >
          ×
        </button>

        <div className="admin-sub-header">
          <h2 id="admin-sub-modal-title">Subscription bearbeiten (Admin)</h2>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="admin-raw-fields">
           <div className="form-group admin-horizontal-group">
             <label htmlFor="admin-sub-id">Subscription ID</label>
             <input id="admin-sub-id" type="text" value={subscription.subscriptionId} disabled style={{ backgroundColor: 'var(--bg-secondary)' }} />
           </div>
           <div className="form-group admin-horizontal-group">
             <label htmlFor="admin-user-id">User ID</label>
             <input 
               id="admin-user-id"
               type="text" 
               value={formData.userId} 
               onChange={(e) => handleFieldChange('userId', e.target.value)}
             />
           </div>
           
           <div className="admin-flex-row">
             <div className="form-group admin-horizontal-group" style={{ flex: '1' }}>
               <label htmlFor="admin-chat-id">Telegram Chat ID</label>
               <input 
                 id="admin-chat-id"
                 type="text" 
                 value={formData.chatId} 
                 onChange={(e) => handleFieldChange('chatId', e.target.value)}
               />
             </div>
             <div className="form-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', marginBottom: 0 }}>
               <input 
                 type="checkbox" 
                 id="admin-enabled"
                 checked={formData.enabled} 
                 onChange={(e) => handleFieldChange('enabled', e.target.checked)}
               />
               <label htmlFor="admin-enabled" style={{ margin: 0, fontWeight: 'bold' }}>Aktiviert</label>
             </div>
           </div>

           <div className="form-group admin-horizontal-group">
             <label htmlFor="admin-expires-at">Ablaufdatum</label>
             <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
               <input
                 id="admin-expires-at"
                 type="datetime-local"
                 value={formData.expiresAt}
                 onChange={(e) => handleFieldChange('expiresAt', e.target.value)}
                 style={{ flex: 1 }}
               />
               {formData.expiresAt && (
                 <button
                   type="button"
                   onClick={() => handleFieldChange('expiresAt', '')}
                   style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', padding: '4px 8px', fontSize: '12px', color: 'var(--text-muted)' }}
                   title="Ablauf entfernen (kein Ablauf)"
                 >✕ Kein Ablauf</button>
               )}
             </div>
           </div>
        </div>

        <SubscriptionEditorFields
          formData={formData}
          userTier={TIER.PREMIUM} // Admin sees all fields
          isLoading={isLoading}
          onFieldChange={handleFieldChange}
          onFilterChange={handleFilterChange}
          isAdmin={true}
          issues={issues}
          isBaseRentActive={isBaseRentActive}
          onBaseRentToggle={handleBaseRentToggle}
          onConstraintChange={setHasZoneConstraint}
          variant="page"
        />

        <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
          <button onClick={handleDelete} className="admin-btn-danger" disabled={isLoading}>
            Löschen
          </button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={onClose} className="btn-secondary" disabled={isLoading}>Abbrechen</button>
            <button onClick={handleSave} className="btn-primary" disabled={isLoading || issues.some(i => i.type === 'error') || hasZoneConstraint}>
              {isLoading ? 'Speichert...' : 'Admin-Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
